import { Schema } from "effect";

export const TUNNEL_CONTAINER_CANARY_PREFIX = "/api/tunnel-container-canary";
export const TUNNEL_CONTAINER_ORIGIN = "http://tunnel-container.invalid";
export const TUNNEL_CONTAINER_REMEDIATION_PLAN_REVISION = "tunnel-container-remediation-r1" as const;
export const TUNNEL_CONTAINER_REMEDIATION_SCOPE = "tunnel-canary-remediation-r1" as const;
export const TUNNEL_CANARY_MAX_INPUT_BYTES = 2 * 1024;
export const TUNNEL_CANARY_CASES = ["authentication", "lifecycle", "proxy", "timeout", "redirect", "cancellation", "outage", "restart", "cleanup"] as const;
export const TUNNEL_CANARY_PROFILES = ["cold", "sequential-full", "edit", "concurrent", "streaming"] as const;
export const TUNNEL_CANARY_CHECKS = Object.freeze({
  authentication: ["valid-health", "invalid-signature", "executor-not-contacted"],
  lifecycle: ["ensure", "port-wait", "state", "renew", "stop", "destroy", "generation-replacement", "capacity", "scope-cleanup", "quota", "image-volume"],
  proxy: ["method", "status", "headers", "bounded-body", "no-fallback"],
  timeout: ["deadline-enforced", "no-retry", "stream-released"],
  redirect: ["redirect-rejected", "no-fallback"],
  cancellation: ["caller-cancelled", "no-retry", "stream-released"],
  outage: ["remote-unavailable", "no-fallback", "no-workload-created"],
  restart: ["connector-ready", "runtime-identity-preserved", "proxy-ready"],
  cleanup: ["scope-empty", "lease-empty", "operation-empty"]
} as const);

const WorkerVersion = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/)).pipe(Schema.brand("CanaryWorkerVersion")))();
const VpcServiceId = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/)).pipe(Schema.brand("CanaryVpcServiceId")))();
const Digest = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/)).pipe(Schema.brand("CanaryDigest")))();
const DeploymentId = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9._-]{0,63}$/)).pipe(Schema.brand("CanaryDeploymentId")))();
export const TunnelCanaryCase = /* @__PURE__ */ (() => Schema.Literals(TUNNEL_CANARY_CASES))();
export type TunnelCanaryCase = typeof TunnelCanaryCase.Type;
export const TunnelCanaryProfile = /* @__PURE__ */ (() => Schema.Literals(TUNNEL_CANARY_PROFILES))();
export type TunnelCanaryProfile = typeof TunnelCanaryProfile.Type;
export const TunnelCanaryMetadata = /* @__PURE__ */ (() => Schema.Struct({
  planRevision: Schema.Literal(TUNNEL_CONTAINER_REMEDIATION_PLAN_REVISION), sourceSha256: Digest,
  scope: Schema.Literal(TUNNEL_CONTAINER_REMEDIATION_SCOPE), backend: Schema.Literal("tunnel"),
  transport: Schema.Literal("stable-http"), workerVersion: WorkerVersion, vpcServiceId: VpcServiceId,
  tunnelIdentitySha256: Digest, runtimeDeployment: DeploymentId
}))();
export type TunnelCanaryMetadata = typeof TunnelCanaryMetadata.Type;
export const TunnelCanaryEnvironment = /* @__PURE__ */ (() => Schema.Struct({
  TUNNEL_CONTAINER_CANARY_PLAN_REVISION: TunnelCanaryMetadata.fields.planRevision,
  TUNNEL_CONTAINER_CANARY_SOURCE_SHA256: Digest, TUNNEL_CONTAINER_CANARY_SCOPE: TunnelCanaryMetadata.fields.scope,
  TUNNEL_CONTAINER_CANARY_VPC_SERVICE_ID: VpcServiceId, TUNNEL_CONTAINER_CANARY_TUNNEL_IDENTITY_SHA256: Digest,
  TUNNEL_CONTAINER_CANARY_RUNTIME_DEPLOYMENT: DeploymentId, CF_VERSION_METADATA: Schema.Struct({ id: WorkerVersion })
}))();
export const TunnelCanaryInput = /* @__PURE__ */ (() => Schema.Struct({
  schemaVersion: Schema.Literal(1), case: Schema.Literals([...TUNNEL_CANARY_CASES, "streaming"]),
  scope: Schema.Literal(TUNNEL_CONTAINER_REMEDIATION_SCOPE)
}))();
export const TunnelCanarySuccess = /* @__PURE__ */ (() => Schema.Struct({
  schemaVersion: Schema.Literal(1), case: TunnelCanaryCase, passed: Schema.Literal(true),
  metadata: TunnelCanaryMetadata, checks: Schema.Record(Schema.String, Schema.Literal("passed"))
}))();
export const TunnelCanaryFailure = /* @__PURE__ */ (() => Schema.Struct({
  schemaVersion: Schema.Literal(1), case: Schema.String, passed: Schema.Literal(false),
  error: Schema.Literal("Tunnel Container canary assertion failed.")
}))();
